// The engine's OWN classification, not a copy of it. Two implementations of
// "does this beat that" drift, and the day they do the board highlights a play
// the server rejects. This subpath is the pure rules module: no state, no RNG,
// no node builtins, so it bundles for the browser unchanged.
import { beats, classify } from '@tira/engines/pusoydos/rules';
import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type JSX,
  type ReactNode,
} from 'react';
import { useT, type CopyKey, type T } from '../../i18n/index.js';
import { socket } from '../../net/socket.js';
import { useApp } from '../../state/store.js';
import { GameRoom, rotateSeat } from '../parts/GameRoom.js';
import './PusoyDosBoard.css';

/**
 * S8 — Pusoy Dos on the room stage (design 2c overhead, 4b the 2.5D room).
 * Presentation only: the server decides every play, and a rejected one comes
 * back as an error toast.
 *
 * The navy felt carries exactly two things, and the split between them is the
 * whole reason the two cameras cost one layout. The standing combo was thrown
 * down, so it lies flat in the middle of the table and takes the camera on the
 * chin. A seat's remaining cards are held, so each opponent's fan of backs
 * carries `up` and stands on that seat's own edge — which is the identity
 * transform overhead and a standing fan across the table.
 *
 * The one place this board differs from the dama and tongits boards: the
 * Pusoy Dos view ships NO legal-action list, and that is deliberate. A 13-card
 * hand holds well over a thousand legal plays and the engine refuses to put
 * them on the wire every frame (see view() in packages/engines/src/pusoydos).
 * So the highlighting below is derived here from this seat's own cards and the
 * standing combo — which is safe, because both are already in this seat's view
 * and the derivation is a hint, never an authority. `apply()` re-checks from
 * first principles on the server.
 *
 * Everything the classification depends on is mirrored from the engine's
 * rules.ts, including the two orders that trip up every port: the deuce is the
 * top rank but the straight ladder is ace-high with no wheel and no wrap, so
 * 2-3-4-5-6 is the strongest straight in the game.
 */

/* ── The view, as the engine publishes it ─────────────────────────────────── */

export type ComboType = 'single' | 'pair' | 'trip' | 'five';
export type FiveKind = 'straight' | 'flush' | 'fullhouse' | 'quads' | 'straightflush';

export interface Combo {
  type: ComboType;
  kind: FiveKind | null;
  rank: number;
}

export interface TableCombo {
  seat: number;
  cards: string[];
  combo: Combo;
}

export interface PusoydosView {
  seats: number;
  /** Seat index, or null on the rail. */
  you: number | null;
  hand: string[];
  /** Cards left per seat. The count is public, the cards are not. */
  counts: number[];
  turn: number | null;
  /** Whoever laid the standing combo — they lead again when everyone passes. */
  control: number;
  table: TableCombo | null;
  firstPlay: boolean;
  passed: number[];
  out: number[];
  quit: number[];
  over: { winner: number | null; reason: string } | null;
}

/** A miron arrives with this seat index — no seat, no taps, no "mine". */
const SPECTATOR_SEAT = -1;

/**
 * Where each seat's fan of backs stands, in table pixels on the 470×330 slab,
 * indexed by the chair GameRoom turned that seat into — so a fan is always on
 * the same edge as that seat's stool, standee and pod. The venue seats four and
 * only four (VENUES.pusoydos.seats), so this is the whole ring.
 *
 * Chair 0 is the local player's. Their own count is the hand on the rail, so
 * that spot is only ever used by a miron, who has no rail of their own.
 *
 * That near spot sits further out than the rest. It is the closest point to the
 * eye, so the 2.5D camera scales it up and stands its billboard back across the
 * middle of the table — at y 112 the count pill landed on the played combo, and
 * the combo is the one thing a miron is watching.
 */
const FAN_SPOTS: readonly { x: number; y: number }[] = [
  { x: 0, y: 138 },
  { x: -178, y: 14 },
  { x: 0, y: -96 },
  { x: 178, y: 14 },
];

/** Card backs drawn per fan. Three reads as "a hand" without crowding the rim. */
const FAN_BACKS = 3;

/** Centre-to-centre of the played combo, from the mock's own fan (playP). */
const PLAY_STEP = 32;

export function PusoyDosBoard(): JSX.Element {
  const match = useApp((s) => s.match);
  const t = useT();
  const [sel, setSel] = useState<string[]>([]);

  const view = useMemo(() => readPusoydosView(match?.view), [match?.view]);

  // Any new server event invalidates a selection — the table moved under it.
  useEffect(() => {
    setSel([]);
  }, [match?.seq, match?.matchId]);

  const hand = view?.hand ?? [];
  const table = view?.table ?? null;
  const mySeat = match && match.seat > SPECTATOR_SEAT ? match.seat : null;
  const myTurn = view !== null && mySeat !== null && view.turn === mySeat && view.over === null;
  /** Only the seat holding 3♣ owes the opening; nobody else can satisfy it. */
  const owesOpening = view !== null && view.firstPlay && hand.includes(THREE_CLUBS);

  /**
   * Cards that appear in at least one legal play right now. Enumerated over the
   * whole hand — at most 1,664 groups of the four legal sizes, which is nothing
   * — rather than sampled, so a card is never dimmed while a beat is sitting in
   * it. Recomputed only when the hand or the standing combo actually moves.
   */
  const playable = useMemo(
    // Off the clock nothing is tappable and the CSS dims the whole fan anyway,
    // so the enumeration only runs for the seat that can actually use it.
    () => (myTurn ? playableCards(hand, table?.combo ?? null, owesOpening) : new Set<string>()),
    [myTurn, hand, table?.combo, owesOpening],
  );

  if (!match || !view) {
    return <GameRoom felt={<p className="room__wait">{t('room.waiting.server')}</p>} />;
  }

  const watching = mySeat === null;
  const picked = [...sel].sort(byCardValue);
  const combo = classify(picked);
  const legal =
    myTurn &&
    combo !== null &&
    (!owesOpening || picked.includes(THREE_CLUBS)) &&
    (table === null || (combo.type === table.combo.type && beats(combo, table.combo)));
  const canPass = myTurn && table !== null;
  /** A five that nothing but a stronger five can answer — the garahe's bomba. */
  const bomba = combo !== null && (combo.kind === 'quads' || combo.kind === 'straightflush');

  const send = (action: { kind: 'play'; cards: string[] } | { kind: 'pass' }): void => {
    socket.send({ t: 'match.action', matchId: match.matchId, seq: match.seq, action });
    setSel([]);
  };

  const toggle = (card: string): void => {
    if (!myTurn) return;
    setSel((cur) => (cur.includes(card) ? cur.filter((c) => c !== card) : [...cur, card]));
  };

  /**
   * Replay key for the drop. Changing it remounts the pile, so the slam runs
   * once per play with no timers and no state to fall out of sync with the view.
   */
  const dropKey = table === null ? `sweep-${view.control}` : `${table.seat}:${table.cards.join(',')}`;
  /**
   * What lands with weight (§G3). Wider than `bomba` above on purpose: a flush
   * is a heavy drop to watch even though only quads and a straight flush are
   * worth calling a bomba in the copy.
   */
  const heavy =
    table !== null &&
    (table.combo.kind === 'quads' ||
      table.combo.kind === 'straightflush' ||
      table.combo.kind === 'flush');

  /** The engine deals one count per seat, so this is the ring's real size. */
  const ring = view.counts.length;

  const felt = (
    <>
      {table === null ? null : (
        <div className="pdos-pile" data-fly="pile">
          <div key={dropKey} className="pdos-drop" data-heavy={heavy}>
            {table.cards.map((c, i) => {
              const d = i - (table.cards.length - 1) / 2;
              return (
                <span
                  key={c}
                  className="pdos-play"
                  style={
                    {
                      '--l': `calc(${d * PLAY_STEP}px - var(--cw) / 2)`,
                      '--rot': `${d * 4}deg`,
                      '--z': i + 1,
                    } as CSSProperties
                  }
                >
                  <Baraha code={c} />
                </span>
              );
            })}
          </div>
        </div>
      )}

      {view.counts.map((n, seat) => {
        const chair = rotateSeat(seat, mySeat, ring);
        // Chair 0 is yours; your own count is the fan you are holding.
        if (chair === 0 && !watching) return null;
        const spot = FAN_SPOTS[chair];
        if (!spot) return null;
        const place = view.out.indexOf(seat);
        const done = place >= 0 || view.quit.includes(seat);
        const shown = Math.min(n, FAN_BACKS);
        return (
          <div
            key={seat}
            className="pdos-backs up"
            data-out={done}
            style={{ '--bx': `${spot.x}px`, '--by': `${spot.y}px` } as CSSProperties}
          >
            {/* Low counts are the whole tension of the endgame, so the pill
                turns before anyone has to compare four numbers. */}
            <b data-low={n > 0 && n <= 3}>
              {t('mj.pd.cards', { n })}
              {/* Finishing order first: a seat that is out has stopped passing. */}
              {view.quit.includes(seat) ? (
                <em>{t('mj.pd.quit')}</em>
              ) : place >= 0 ? (
                <em>{t('mj.pd.place', { n: place + 1 })}</em>
              ) : view.passed.includes(seat) ? (
                <em>{t('mj.pd.passed')}</em>
              ) : null}
            </b>
            {Array.from({ length: shown }, (_, k) => {
              const d = k - (shown - 1) / 2;
              return (
                <i
                  key={k}
                  style={{ '--l': `${d * 14 + 14}px`, '--rot': `${d * 12}deg` } as CSSProperties}
                />
              );
            })}
          </div>
        );
      })}
    </>
  );

  return (
    <GameRoom
      felt={felt}
      toast={toastLine(t, view, mySeat, myTurn, watching)}
      hand={
        watching ? undefined : (
          <div className="pdos-hand" data-live={myTurn}>
            {hand.length === 0 ? (
              <p className="pdos-bare">{t('mj.pd.bare')}</p>
            ) : (
              hand.map((c) => (
                <button
                  key={c}
                  className="pdos-card"
                  data-picked={sel.includes(c)}
                  data-playable={playable.has(c)}
                  disabled={!myTurn}
                  onClick={() => toggle(c)}
                  aria-pressed={sel.includes(c)}
                  aria-label={cardName(t, c)}
                >
                  <Baraha code={c} />
                </button>
              ))
            )}
          </div>
        )
      }
      actions={
        myTurn ? (
          <>
            <button
              className="room-act"
              data-tone="primary"
              type="button"
              disabled={!legal}
              onClick={() => legal && send({ kind: 'play', cards: picked })}
            >
              {t('mj.pd.play')}
            </button>
            <button
              className="room-act"
              type="button"
              disabled={!canPass}
              onClick={() => send({ kind: 'pass' })}
            >
              {t('mj.act.pass')}
            </button>
          </>
        ) : null
      }
      aside={
        myTurn ? (
          /*
           * What the current pick is worth, on the mock's own second pill
           * (2c, y 364) — directly above the toast, which is already spoken for
           * by the standing combo. Short strings: this line sits between the
           * toast and the rail and a wrapped second line lands on both.
           */
          <p className="pdos-pick" data-legal={legal} data-bomba={bomba && legal}>
            <span>
              {sel.length === 0
                ? t(owesOpening ? 'mj.pd.pick.open' : 'mj.pd.pick')
                : combo === null
                  ? t('mj.pd.pick.bad')
                  : legal
                    ? bomba
                      ? t('mj.pd.pick.bomba', { combo: comboName(t, combo) })
                      : comboName(t, combo)
                    : table === null
                      ? t('mj.pd.pick.needthree')
                      : t('mj.pd.pick.weak')}
            </span>
            {sel.length > 0 ? (
              <button type="button" onClick={() => setSel([])}>
                {t('mj.pd.clear')}
              </button>
            ) : null}
          </p>
        ) : null
      }
    />
  );
}

/* ── The card ─────────────────────────────────────────────────────────────── */

/**
 * Plastic-coated face, four-colour suits (component sheet 1d). The glyph is the
 * read; the hue is a redundant second cue, so nothing depends on telling red
 * from black (§7.3).
 *
 * Duplicated from TongitsBoard rather than shared: the two boards are the only
 * consumers today and neither owns components/. It belongs in a primitive the
 * moment a third baraha venue lands.
 */
function Baraha({ code }: { code: string }): JSX.Element {
  const suit = code.slice(-1);
  return (
    <span className="baraha" data-suit={suit} aria-hidden="true">
      <span className="baraha__idx">
        <b>{code.slice(0, -1)}</b>
        <i>{SUIT_GLYPH[suit] ?? '?'}</i>
      </span>
      <span className="baraha__mid">{SUIT_GLYPH[suit] ?? '?'}</span>
    </span>
  );
}

const SUIT_GLYPH: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_KEY: Record<string, CopyKey> = {
  S: 'mj.pd.suit.S',
  H: 'mj.pd.suit.H',
  D: 'mj.pd.suit.D',
  C: 'mj.pd.suit.C',
};

function cardName(t: T, code: string): string {
  const suit = SUIT_KEY[code.slice(-1)];
  return t('mj.pd.card', { rank: code.slice(0, -1), suit: suit === undefined ? '?' : t(suit) });
}

/* ── Rules mirror — hints only, never authority ───────────────────────────── */

const THREE_CLUBS = '3C';

/** Low→high. The deuce tops the ladder; the ace sits under it. */
const PD_RANKS = ['3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A', '2'];
/** ♣ < ♠ < ♥ < ♦ — every rank tie between singles falls through to this. */
const PD_SUITS: Record<string, number> = { C: 0, S: 1, H: 2, D: 3 };
const FIVE_ORDER: FiveKind[] = ['straight', 'flush', 'fullhouse', 'quads', 'straightflush'];
const SIZE_OF: Record<ComboType, number> = { single: 1, pair: 2, trip: 3, five: 5 };

/**
 * Straight ladder — and the one place the deuce is NOT high. Five consecutive
 * positions on 2,3,…,K,A: the wheel A-2-3-4-5 and the wrap J-Q-K-A-2 are not
 * straights. Detection is on this ladder; ranking is on cardValue below, which
 * is why 2-3-4-5-6 outranks 10-J-Q-K-A.
 */
const LADDER: Record<string, number> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  '10': 10, J: 11, Q: 12, K: 13, A: 14,
};

const rankOf = (code: string): string => code.slice(0, -1);
const suitOf = (code: string): string => code.slice(-1);

function cardValue(code: string): number {
  return PD_RANKS.indexOf(rankOf(code)) * 4 + (PD_SUITS[suitOf(code)] ?? 0);
}

const byCardValue = (a: string, b: string): number => cardValue(a) - cardValue(b);


function playableCards(
  hand: readonly string[],
  table: Combo | null,
  mustHoldThreeClubs: boolean,
): Set<string> {
  const out = new Set<string>();
  const sizes = table === null ? [1, 2, 3, 5] : [SIZE_OF[table.type]];
  for (const k of sizes) {
    for (const group of combinations(hand, k)) {
      if (mustHoldThreeClubs && !group.includes(THREE_CLUBS)) continue;
      const combo = classify(group);
      if (!combo) continue;
      if (table && !beats(combo, table)) continue;
      for (const c of group) out.add(c);
    }
  }
  return out;
}

/** Every k-subset of `hand`, in index order, so a sorted hand yields sorted groups. */
function* combinations(hand: readonly string[], k: number): Generator<string[]> {
  const n = hand.length;
  if (k <= 0 || k > n) return;
  const idx = Array.from({ length: k }, (_, i) => i);
  for (;;) {
    const group: string[] = [];
    for (const i of idx) {
      const c = hand[i];
      if (c !== undefined) group.push(c);
    }
    if (group.length === k) yield group;
    let i = k - 1;
    while (i >= 0 && (idx[i] ?? 0) === n - k + i) i--;
    if (i < 0) return;
    idx[i] = (idx[i] ?? 0) + 1;
    for (let j = i + 1; j < k; j++) idx[j] = (idx[j - 1] ?? 0) + 1;
  }
}

const FIVE_LABEL: Record<FiveKind, CopyKey> = {
  straight: 'mj.pd.combo.straight',
  flush: 'mj.pd.combo.flush',
  fullhouse: 'mj.pd.combo.fullhouse',
  quads: 'mj.pd.combo.quads',
  straightflush: 'mj.pd.combo.straightflush',
};

function comboName(t: T, c: Combo): string {
  if (c.type === 'single') return t('mj.pd.combo.single');
  if (c.type === 'pair') return t('mj.pd.combo.pair');
  if (c.type === 'trip') return t('mj.pd.combo.trip');
  return t(c.kind === null ? 'mj.pd.combo.five' : FIVE_LABEL[c.kind]);
}

/* ── Reading the engine's view ────────────────────────────────────────────── */

/**
 * The view arrives as `unknown` (the wire type must not know the rules), so it
 * is narrowed rather than cast. A malformed payload renders the waiting state
 * instead of throwing inside a live match.
 */
export function readPusoydosView(raw: unknown): PusoydosView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const v = raw as Record<string, unknown>;
  if (!Array.isArray(v['counts'])) return null;

  return {
    seats: isNum(v['seats']) ? v['seats'] : v['counts'].length,
    you: isNum(v['you']) ? v['you'] : null,
    hand: cardList(v['hand']),
    counts: v['counts'].filter(isNum),
    turn: isNum(v['turn']) ? v['turn'] : null,
    control: isNum(v['control']) ? v['control'] : 0,
    table: readTable(v['table']),
    firstPlay: v['firstPlay'] === true,
    passed: numList(v['passed']),
    out: numList(v['out']),
    quit: numList(v['quit']),
    over: readOver(v['over']),
  };
}

function readTable(raw: unknown): TableCombo | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const t = raw as Record<string, unknown>;
  const cards = cardList(t['cards']);
  if (!isNum(t['seat']) || cards.length === 0) return null;
  // The engine sends its own classification; falling back to ours keeps a
  // slightly older or newer server from blanking the pile.
  return { seat: t['seat'], cards, combo: readCombo(t['combo']) ?? classify(cards) ?? FALLBACK };
}

const FALLBACK: Combo = { type: 'single', kind: null, rank: 0 };

function readCombo(raw: unknown): Combo | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const c = raw as Record<string, unknown>;
  const type = c['type'];
  if (type !== 'single' && type !== 'pair' && type !== 'trip' && type !== 'five') return null;
  const kind = FIVE_ORDER.find((k) => k === c['kind']) ?? null;
  return { type, kind, rank: isNum(c['rank']) ? c['rank'] : 0 };
}

function readOver(raw: unknown): { winner: number | null; reason: string } | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  return {
    winner: isNum(o['winner']) ? o['winner'] : null,
    reason: typeof o['reason'] === 'string' ? o['reason'] : 'ubos',
  };
}

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const isCard = (x: unknown): x is string => typeof x === 'string' && x.length >= 2;
const cardList = (x: unknown): string[] => (Array.isArray(x) ? x.filter(isCard) : []);
const numList = (x: unknown): number[] => (Array.isArray(x) ? x.filter(isNum) : []);

/* ── Copy ─────────────────────────────────────────────────────────────────── */

function seatName(t: T, seat: number, mySeat: number | null, watching: boolean): string {
  if (!watching && seat === mySeat) return t('room.you');
  return t('room.seat', { n: seat + 1 });
}

/**
 * The one placeholder `t` was not given, rendered as an element instead. The
 * standing combo carries the toast's gold weight, and each language puts it in
 * a different place in the sentence.
 */
function withCombo(line: string, combo: string): JSX.Element {
  const [head = '', tail = ''] = line.split('{combo}');
  return (
    <>
      {head}
      <b>{combo}</b>
      {tail}
    </>
  );
}

/**
 * The room's one line. It carries the combo to beat and whose it is, because
 * that is the question the whole venue turns on — the mock's "Marites played a
 * straight — beat it or pass" (2c). Whose clock it is stays on the pods, which
 * both cameras already show.
 */
function toastLine(
  t: T,
  v: PusoydosView,
  mySeat: number | null,
  myTurn: boolean,
  watching: boolean,
): ReactNode {
  if (v.over !== null) {
    return v.over.winner === null
      ? t('mj.pd.toast.nopot')
      : t('mj.pd.toast.won', { who: seatName(t, v.over.winner, mySeat, watching) });
  }
  if (v.table === null) {
    return !watching && v.control === mySeat
      ? t('mj.pd.toast.open.you')
      : t('mj.pd.toast.open', { who: seatName(t, v.control, mySeat, watching) });
  }
  const what = comboName(t, v.table.combo);
  if (!watching && v.table.seat === mySeat) {
    return withCombo(t('mj.pd.toast.yours'), what);
  }
  const who = seatName(t, v.table.seat, mySeat, watching);
  if (myTurn) return withCombo(t('mj.pd.toast.beat', { who }), what);
  return withCombo(
    v.turn === null
      ? t('mj.pd.toast.next', { who })
      : t('mj.pd.toast.waiting', { who, next: seatName(t, v.turn, mySeat, watching) }),
    what,
  );
}
